/**
 * Clinic Information Page
 *
 * 治療院基本情報 - マスタメンテナンス/治療院基本情報
 * Displays clinic basic information with editable email and password change.
 * Uses auth/user (already loaded on site entry) for user, site_type, service_type; list API for clinic info by page.
 */

import { AppPagination, AppSpin } from '@/components/atoms';
import {
  useMainClinicInformationChangePassword,
  useMainClinicInformationList,
  useMainClinicInformationUpdateEmail,
} from '@/hooks/main';
import {
  changePasswordFormSchema,
  emailUpdateFormSchema,
  type ChangePasswordFormData,
  type EmailUpdateFormData,
} from '@/schemas/clinicInformationSchema';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTrimmedForm } from '@/shared/lib/useTrimmedForm';
import { useAuth } from '@shared/providers/AuthContext';
import { useTheme } from '@shared/providers/theme/ThemeContext';
import React, { useCallback, useEffect, useMemo } from 'react';

import ClinicInformationBankSection from './ClinicInformationBankSection';
import ClinicInformationDetailSection from './ClinicInformationDetailSection';
import ClinicInformationUserSection from './ClinicInformationUserSection';
import type { ClinicInformation, ClinicInformationPagination, UserInfo } from './types';

const ERROR_MESSAGE = 'データの取得に失敗しました。';

interface PageContent {
  clinicInfo: ClinicInformation | null;
  pagination: ClinicInformationPagination | null;
}

const ClinicInformationPage: React.FC = () => {
  const { user, clinic } = useAuth();
  const [currentPage, setCurrentPage] = React.useState(1);
  const {
    data: listResponse,
    isLoading: isListLoading,
    isFetching: isListFetching,
    isError: isListError,
  } = useMainClinicInformationList(currentPage);
  const { updateEmailMutation, isLoading: updateEmailPending } =
    useMainClinicInformationUpdateEmail();
  const { changePasswordMutation, isLoading: changePasswordPending } =
    useMainClinicInformationChangePassword();
  const { colors } = useTheme();

  const [pageContent, setPageContent] = React.useState<PageContent>({
    clinicInfo: null,
    pagination: null,
  });

  const emailForm = useTrimmedForm<EmailUpdateFormData>({
    resolver: zodResolver(emailUpdateFormSchema),
    mode: 'onBlur',
    defaultValues: { email: '' },
  });

  const passwordForm = useTrimmedForm<ChangePasswordFormData>({
    resolver: zodResolver(changePasswordFormSchema),
    mode: 'onBlur',
    defaultValues: { password: '', password_confirmation: '' },
  });

  // Derived from auth/user (already loaded on site entry) – no init API
  const initData = useMemo(() => {
    const siteType = clinic?.site_type;
    const isUpsystem = siteType === 'upsystem' || siteType === 'up-system';
    return {
      user: user
        ? ({ login_id: (user as { login_id?: string }).login_id ?? '' } as UserInfo)
        : null,
      is_upsystem: isUpsystem,
      site_type: siteType,
      service_type: clinic?.service_type,
    };
  }, [user, clinic]);

  // Sync from list for any page (1, 2, …)
  useEffect(() => {
    if (isListLoading || isListError || !listResponse?.data?.data?.length) {
      if (listResponse?.meta) {
        setPageContent(prev => ({ ...prev, pagination: listResponse.meta }));
      }
      return;
    }
    // Reverse order: show newest version first (opposite of admin site)
    const reversedData = [...listResponse.data.data].reverse();
    const item = reversedData[0];
    setPageContent({ clinicInfo: item, pagination: listResponse.meta });
    emailForm.reset({ email: item.email ?? '' });
  }, [isListLoading, isListError, listResponse]);

  const onEmailSubmit = useCallback(
    (data: EmailUpdateFormData) => {
      const info = pageContent.clinicInfo;
      if (!info) return;
      updateEmailMutation({ id: info.id, data: { email: data.email } }).then(response => {
        if (response?.success) {
          setPageContent(prev =>
            prev.clinicInfo
              ? { ...prev, clinicInfo: { ...prev.clinicInfo, email: data.email } }
              : prev
          );
        }
      });
    },
    [updateEmailMutation, pageContent.clinicInfo?.id]
  );

  const onPasswordSubmit = useCallback(
    (data: ChangePasswordFormData) => {
      changePasswordMutation({
        password: data.password,
        password_confirmation: data.password_confirmation,
      }).then(response => {
        if (response?.success) passwordForm.reset();
      });
    },
    [changePasswordMutation]
  );

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
    emailForm.reset({ email: '' });
    if (page > 1) {
      setPageContent(prev => ({ ...prev, clinicInfo: null }));
    }
  }, []);

  const siteType = initData.site_type;
  const isUpsystemSite =
    siteType === 'upsystem' ||
    siteType === 'up-system' ||
    clinic?.site_type === 'upsystem' ||
    clinic?.site_type === 'up-system';
  const showBankSection = Boolean(
    pageContent.clinicInfo && (initData.is_upsystem || isUpsystemSite)
  );
  const isPageLoading = isListLoading;
  const { clinicInfo, pagination } = pageContent;

  if (isPageLoading) {
    return (
      <div
        className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 rounded-md bg-white/80"
        aria-busy="true"
      >
        <AppSpin size="large" />
      </div>
    );
  }
  return (
    <div className="relative space-y-4">
      {isListFetching && (
        <div
          className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 rounded-md bg-white/60"
          aria-busy="true"
        >
          <AppSpin size="large" />
        </div>
      )}
      {isListError && (
        <div className="px-4 py-3 text-red-700 border border-red-200 rounded bg-red-50">
          {ERROR_MESSAGE}
        </div>
      )}

      <div className="p-4 bg-white rounded shadow-md">
        <ClinicInformationUserSection
          loginId={initData.user?.login_id}
          control={passwordForm.control}
          errors={passwordForm.formState.errors}
          onSubmit={passwordForm.handleSubmit(onPasswordSubmit)}
          isPending={changePasswordPending}
        />
      </div>

      <div className="p-4 space-y-5 bg-white rounded shadow-md">
        {clinicInfo && (
          <ClinicInformationDetailSection
            clinicInfo={clinicInfo}
            serviceType={initData.service_type ?? clinic?.service_type}
            control={emailForm.control}
            errors={emailForm.formState.errors}
            onSubmit={emailForm.handleSubmit(onEmailSubmit)}
            isPending={updateEmailPending}
            primaryButtonColor={colors.primary}
          />
        )}

        {showBankSection && clinicInfo && <ClinicInformationBankSection clinicInfo={clinicInfo} />}

        <div className="flex justify-center mt-4">
          <AppPagination
            current={pagination?.current_page}
            total={pagination?.total}
            pageSize={pagination?.per_page}
            onChange={page => handlePageChange(page ?? 1)}
          />
        </div>
      </div>
    </div>
  );
};

export default ClinicInformationPage;
